import React from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Video, Search, UserCheck, Rocket, ArrowRight, CheckCircle2 } from 'lucide-react';

const Process = () => {
  const steps = [
    {
      number: '01',
      icon: <Video className="w-10 h-10" />,
      title: 'Discovery Call',
      description: 'Meet with one of our bilingual specialists via Zoom to discuss your business needs and staffing goals.',
      image: 'https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b',
      details: [
        'Discuss your current operations',
        'Identify staffing challenges',
        'Define role requirements',
        'Understand your workflow',
        'Establish timeline and budget'
      ]
    },
    {
      number: '02',
      icon: <Search className="w-10 h-10" />,
      title: 'Talent Matching',
      description: 'We identify qualified professionals based on your operational requirements.',
      image: 'https://images.unsplash.com/photo-1612831455359-970e23a1e4e9',
      details: [
        'Review our talent pool',
        'Match skills to your needs',
        'Verify qualifications',
        'Conduct preliminary screening',
        'Present top candidates'
      ]
    },
    {
      number: '03',
      icon: <UserCheck className="w-10 h-10" />,
      title: 'Interview Process',
      description: 'You interview selected candidates and choose the best fit for your organization.',
      image: 'https://images.pexels.com/photos/7693692/pexels-photo-7693692.jpeg',
      details: [
        'Schedule video interviews',
        'Evaluate candidates',
        'Assess culture fit',
        'Technical skill verification',
        'Make your selection'
      ]
    },
    {
      number: '04',
      icon: <Rocket className="w-10 h-10" />,
      title: 'Onboarding & Integration',
      description: 'Your new team member integrates into your workflow and begins supporting your operations.',
      image: 'https://images.pexels.com/photos/36733304/pexels-photo-36733304.jpeg',
      details: [
        'Complete documentation',
        'Systems training',
        'Workflow integration',
        'Performance monitoring',
        'Ongoing support'
      ]
    }
  ];

  return (
    <div className="process-page">
      {/* Hero Section */}
      <section className="page-hero py-24 bg-gradient-to-br from-cyan-50 to-green-50">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-5xl lg:text-6xl font-bold mb-6">
            How It <span className="text-gradient">Works</span>
          </h1>
          <p className="text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto">
            A simple, streamlined process to build your remote team
          </p>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="space-y-32">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`step-item grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''} relative`}>
                  <div className="step-number text-9xl font-bold text-gray-100 absolute -top-16 -left-4 z-0">
                    {step.number}
                  </div>
                  <div className="relative z-10">
                    <div className="icon-wrapper w-20 h-20 rounded-2xl bg-gradient-to-r from-cyan-500 to-green-500 flex items-center justify-center mb-6 text-white">
                      {step.icon}
                    </div>
                    <h2 className="text-4xl lg:text-5xl font-bold mb-6">{step.title}</h2>
                    <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                      {step.description}
                    </p>
                    <div className="space-y-4">
                      {step.details.map((detail, dIndex) => (
                        <div key={dIndex} className="flex items-start gap-3">
                          <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                          <span className="text-lg text-gray-700">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="image-wrapper rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Visual */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">Typical Timeline</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From first call to fully integrated team member in as little as 2-3 weeks
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-cyan-500 to-green-500 hidden lg:block"></div>
              <div className="space-y-12">
                {[
                  { day: 'Day 1-2', title: 'Discovery Call', description: 'Initial consultation and needs assessment' },
                  { day: 'Day 3-7', title: 'Candidate Selection', description: 'We identify and screen qualified candidates' },
                  { day: 'Day 8-12', title: 'Interviews', description: 'You meet and evaluate top candidates' },
                  { day: 'Day 13-21', title: 'Onboarding', description: 'New hire integration and training' }
                ].map((item, index) => (
                  <div key={index} className="relative">
                    <Card className="lg:w-5/12 p-6 hover:shadow-xl transition-shadow duration-300" style={{ marginLeft: index % 2 === 0 ? '0' : 'auto', marginRight: index % 2 === 0 ? 'auto' : '0' }}>
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-cyan-500 to-green-500 flex items-center justify-center text-white font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-cyan-600">{item.day}</div>
                          <div className="text-xl font-bold">{item.title}</div>
                        </div>
                      </div>
                      <p className="text-gray-600">{item.description}</p>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-24 bg-gradient-to-r from-cyan-500 to-green-500 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">Start Building Your Remote Team Today</h2>
          <p className="text-xl lg:text-2xl mb-10 opacity-90 max-w-3xl mx-auto">
            Schedule a free consultation and take the first step toward scaling your operations.
          </p>
          <Button size="lg" className="bg-white text-cyan-600 hover:bg-gray-100 text-lg px-10 py-6 font-semibold">
            Book Your Discovery Call
            <ArrowRight className="ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Process;